import axios from "axios";

/**
 * Upload helpers — all uploads use https://upload.myskool.app
 * Response paths are read via getUploadPath() from myresp[].path
 */

export const MYSKOOL_UPLOAD_URL = "https://upload.myskool.app";

/** @deprecated Use MYSKOOL_UPLOAD_URL — kept so existing imports keep working */
export const GDRIVE_UPLOAD_URL = MYSKOOL_UPLOAD_URL;
/** @deprecated Not used with upload.myskool.app */
export const GDRIVE_UPLOAD_API_KEY = "";
/** @deprecated Not used with upload.myskool.app */
export const GDRIVE_UPLOAD_FOLDER = "myskool";

export function normalizeUploadFiles(files) {
  if (!files) return [];
  if (files instanceof File) return [files];
  if (files instanceof FileList || Array.isArray(files)) {
    return Array.from(files);
  }
  return [];
}

function parseUploadResponse(data) {
  if (data?.status === "success" && Array.isArray(data.myresp)) {
    return data.myresp;
  }
  if (Array.isArray(data?.myresp)) {
    return data.myresp;
  }
  if (data?.path) {
    return [data];
  }
  throw new Error(data?.message || "Upload failed");
}

export async function uploadToMySkool(files) {
  const fileList = normalizeUploadFiles(files);
  if (!fileList.length) {
    throw new Error("No files selected");
  }

  const formData = new FormData();
  fileList.forEach((file) => {
    formData.append("file", file);
  });

  const response = await axios.post(MYSKOOL_UPLOAD_URL, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return parseUploadResponse(response?.data);
}

/** @deprecated Use uploadToMySkool — kept for existing imports */
export async function uploadToGDrive(files) {
  return uploadToMySkool(files);
}

export function getUploadPath(item) {
  return item?.path || item?.url || "";
}

export function getUploadedPaths(myresp) {
  return (myresp || []).map(getUploadPath).filter(Boolean);
}

export function getMediaTypeFromName(nameOrUrl) {
  const value = String(nameOrUrl || "").split("?")[0];
  if (/\.(jpg|jpeg|png|webp|avif|gif|svg|ico)$/i.test(value)) return "Image";
  if (/\.(mp4|amv|webm|mov)$/i.test(value)) return "Video";
  if (/\.(pdf|doc|ppt|docx|xls|css|txt|xlsx)$/i.test(value)) return "pdf";
  return "File";
}

export function getMediaTypeFromMime(mimetype) {
  const mime = String(mimetype || "").toLowerCase();
  if (mime.startsWith("image/")) return "Image";
  if (mime.startsWith("video/")) return "Video";
  if (mime === "application/pdf") return "pdf";
  if (
    mime.includes("word") ||
    mime.includes("document") ||
    mime.includes("presentation") ||
    mime.includes("spreadsheet") ||
    mime.includes("ms-excel") ||
    mime.includes("msword")
  ) {
    return "pdf";
  }
  return null;
}

export function getUploadedMediaType(item) {
  if (!item) return "File";
  const fromMime = getMediaTypeFromMime(item.mimetype);
  if (fromMime) return fromMime;

  const nameSource = item.originalname || item.path || item.url || "";
  const fromName = getMediaTypeFromName(nameSource);
  if (fromName !== "File") return fromName;

  const hashMatch = String(nameSource).match(/#\/([^?&#]+)/);
  if (hashMatch && hashMatch[1]) {
    const fromHash = getMediaTypeFromName(hashMatch[1]);
    if (fromHash !== "File") return fromHash;
  }

  return "File";
}

export function extractGoogleDriveFileId(url) {
  if (!url) return null;
  const value = String(url);

  let match = value.match(/[?&]id=([^&]+)/);
  if (match) return match[1];

  match = value.match(/\/file\/d\/([^/?#]+)/);
  if (match) return match[1];

  match = value.match(/\/d\/([^/?#=]+)/);
  if (match) return match[1];

  return null;
}

export function getDriveImagePreviewCandidates(url) {
  const id = extractGoogleDriveFileId(url);
  if (!id) return [url].filter(Boolean);
  return [
    `https://lh3.googleusercontent.com/d/${id}=w400`,
    `https://drive.google.com/thumbnail?id=${id}&sz=w1000`,
    `https://drive.google.com/uc?export=view&id=${id}`,
    url,
  ].filter(Boolean);
}

export function getDrivePreviewUrl(url) {
  const candidates = getDriveImagePreviewCandidates(url);
  return candidates[0] || url;
}

export function getMediaPreviewUrl(url, type) {
  if (!url || String(url).startsWith("blob:")) return url || "";
  const mediaType = String(type || "").toLowerCase();
  if (mediaType === "image") {
    const value = String(url);
    if (
      !extractGoogleDriveFileId(value) &&
      !/googleusercontent\.com/i.test(value)
    ) {
      return value;
    }
    return getDrivePreviewUrl(value);
  }
  return url;
}

export function handleDrivePreviewError(event, url, type) {
  const img = event?.target;
  if (!img) return;

  const mediaType = String(type || "").toLowerCase();
  if (mediaType !== "image") return;

  const candidates = getDriveImagePreviewCandidates(url);
  const tried = img.getAttribute("data-preview-index");
  const nextIndex = tried === null ? 1 : Number(tried) + 1;

  if (candidates[nextIndex]) {
    img.setAttribute("data-preview-index", String(nextIndex));
    img.src = candidates[nextIndex];
    return;
  }
}

export function buildUploadedAttachment(item, extra = {}) {
  const url = getUploadPath(item);
  let type = getUploadedMediaType(item);
  if (type === "File" && isImageUrl(url, type)) type = "Image";
  if (type === "File" && isPdfUrl(url, type)) type = "pdf";
  return {
    url,
    type,
    previewUrl: getMediaPreviewUrl(url, type),
    fileSize: item?.size,
    ...extra,
  };
}

export function getDocumentDisplayName(url, fallbackIndex) {
  if (!url) {
    return fallbackIndex != null ? `Document ${fallbackIndex + 1}` : "Document";
  }

  const value = String(url);
  const hashMatch = value.match(/#\/([^?&#]+)/) || value.match(/#([^?&#/]+)$/);
  if (hashMatch && hashMatch[1]) {
    try {
      const decoded = decodeURIComponent(hashMatch[1]);
      if (decoded && !decoded.startsWith("http")) return decoded;
    } catch (e) {
      if (hashMatch[1] && !hashMatch[1].startsWith("http")) return hashMatch[1];
    }
  }

  const pathPart = value.split("?")[0].split("/").pop() || "";
  if (/\.[a-z0-9]{2,5}$/i.test(pathPart) && pathPart.length < 80) {
    try {
      return decodeURIComponent(pathPart);
    } catch (e) {
      return pathPart;
    }
  }

  const type = getMediaTypeFromName(value);
  if (type === "pdf") {
    return fallbackIndex != null
      ? `Document ${fallbackIndex + 1}.pdf`
      : "PDF Document";
  }
  if (type === "Image") {
    return fallbackIndex != null ? `Image ${fallbackIndex + 1}` : "Image";
  }

  if (extractGoogleDriveFileId(value)) {
    if (isImageUrl(value)) {
      return fallbackIndex != null ? `Image ${fallbackIndex + 1}` : "Image";
    }
    return fallbackIndex != null ? `Document ${fallbackIndex + 1}` : "Document";
  }

  if (pathPart.length > 40) return `${pathPart.slice(0, 37)}...`;
  return pathPart || (fallbackIndex != null ? `Document ${fallbackIndex + 1}` : "File");
}

export function isLikelyGDriveMediaUrl(url) {
  return /drive\.google\.com|googleusercontent\.com|cloudious\.net|gdrive\./i.test(
    String(url || "")
  );
}

export function isPdfUrl(url, type) {
  const mediaType = String(type || "").toLowerCase();
  if (mediaType === "pdf") return true;
  return /\.(pdf|doc|ppt|docx|xls|xlsx)(\?|#|$)/i.test(String(url || ""));
}

export function getDocumentTypeLabel(url, displayName) {
  const name = displayName || getDocumentDisplayName(url);
  let type = getMediaTypeFromName(name) || getMediaTypeFromName(url);
  if (type === "File" && isImageUrl(url, type)) type = "Image";
  if (type === "Image") return "IMG";
  if (type === "Video") return "VID";
  if (type === "pdf" || isPdfUrl(url, type)) return "PDF";
  return "FILE";
}

export function normalizeDocAttachment(entry) {
  if (!entry) return { url: "", type: "File", previewUrl: "" };

  if (typeof entry === "object") {
    const url = getUploadPath(entry);
    let type =
      entry.type ||
      getUploadedMediaType(entry) ||
      getMediaTypeFromName(entry.originalname || url);
    if (type === "File" && isImageUrl(url, type)) type = "Image";
    if (type === "File" && isPdfUrl(url, type)) type = "pdf";
    return {
      url,
      type,
      previewUrl: entry.previewUrl || getMediaPreviewUrl(url, type),
    };
  }

  const url = String(entry);
  let type = getMediaTypeFromName(url);
  if (type === "File" && isImageUrl(url, type)) type = "Image";
  if (type === "File" && isPdfUrl(url, type)) type = "pdf";
  return {
    url,
    type,
    previewUrl: getMediaPreviewUrl(url, type),
  };
}

export function parseDocUrls(attachment) {
  if (!attachment) return [];
  return String(attachment)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((url) => normalizeDocAttachment(url));
}

export function serializeDocUrls(docList) {
  return (docList || [])
    .map((entry) => normalizeDocAttachment(entry).url)
    .filter(Boolean)
    .join(",");
}

export function isImageMediaType(type) {
  return String(type || "").toLowerCase() === "image";
}

export function isImageUrl(url, type) {
  if (isImageMediaType(type)) return true;
  if (isPdfUrl(url, type)) return false;
  if (getMediaTypeFromName(url) === "Image") return true;

  const value = String(url || "");
  const hashMatch = value.match(/#\/([^?&#]+)/) || value.match(/#([^?&#/]+)$/);
  if (hashMatch && hashMatch[1]) {
    const hashType = getMediaTypeFromName(hashMatch[1]);
    if (hashType === "Image") return true;
    if (hashType === "pdf") return false;
  }

  if (/export=view|thumbnail\?id=|googleusercontent\.com/i.test(value)) {
    return !isPdfUrl(url, type);
  }

  return false;
}

export function isPreviewableDoc(entry) {
  const doc = normalizeDocAttachment(entry);
  return isImageUrl(doc.url, doc.type);
}

export function getDocPreviewUrl(entry) {
  return normalizeDocAttachment(entry).previewUrl;
}

export function getDocUrl(entry) {
  return normalizeDocAttachment(entry).url;
}

export function normalizeAttachmentRecord(record) {
  if (!record) return record;
  const url = record.url || record.path || "";
  const knownTypes = ["Image", "Video", "pdf"];
  let type = record.type;

  if (!type || type === "File" || type === "Link") {
    if (knownTypes.includes(record.name)) {
      type = record.name;
    } else {
      type = getUploadedMediaType({
        path: url,
        mimetype: record.mimetype,
        originalname: record.resourceName || record.originalname,
      });
    }
  }

  return {
    ...record,
    url,
    type,
    previewUrl: getMediaPreviewUrl(url, type),
  };
}
